
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FloorPlanPreview } from "@/components/dashboard/FloorPlanPreview";
import { Loader2 } from "lucide-react";

interface PreviewDialogProps {
  plan: {
    id: string;
    name: string;
    preview_image: string | null;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PreviewDialog({ plan, open, onOpenChange }: PreviewDialogProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [loadTimeout, setLoadTimeout] = useState<NodeJS.Timeout | null>(null);

  // Reset loading state whenever the dialog opens or plan changes
  useEffect(() => {
    if (open) {
      console.log("PreviewDialog opened for plan:", plan.id);
      setIsLoading(true);
      
      // Set a timeout to ensure we don't wait forever for the preview to load
      const timeout = setTimeout(() => {
        console.log("Preview load timeout reached");
        setIsLoading(false);
      }, 5000);
      
      setLoadTimeout(timeout);
      
      return () => {
        if (loadTimeout) {
          clearTimeout(loadTimeout);
        }
      };
    }
  }, [open, plan.id]);

  // Set a short delay to allow the preview to load
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="w-full">
          Bekijk plattegrond
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {plan.name}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center aspect-[16/9] w-full bg-muted rounded-md">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <FloorPlanPreview
              id={plan.id}
              name={plan.name}
              previewImage={plan.preview_image}
              allowRefresh={false}
              className="aspect-[16/9] w-full"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
